import { ResponsiveBar } from "@nivo/bar";
import { useEffect, useState } from "react";

import { useTheme } from "@/components/admin/use-theme";
import { Button } from "@/components/ui/button";

import { formatEuro } from "../gulaConstants";
import type { MonthBucket } from "./moneyStats";

/** Four series at most: instalments and anything else fold into one. */
const SERIES = [
  { key: "equipment", label: "Equipos", light: "#2a78d6", dark: "#3987e5" },
  { key: "rental", label: "Alquiler", light: "#eb6834", dark: "#d95926" },
  {
    key: "maintenance",
    label: "Mantenimiento",
    light: "#1baf7a",
    dark: "#199e70",
  },
  { key: "rest", label: "Plazos y otros", light: "#eda100", dark: "#c98500" },
] as const;

type ChartRow = {
  label: string;
  equipment: number;
  rental: number;
  maintenance: number;
  rest: number;
  total: number;
};

const useIsDark = () => {
  const { theme } = useTheme();
  const [systemDark, setSystemDark] = useState(
    () => window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false,
  );
  useEffect(() => {
    const query = window.matchMedia?.("(prefers-color-scheme: dark)");
    if (!query) return;
    const listener = (event: MediaQueryListEvent) =>
      setSystemDark(event.matches);
    query.addEventListener("change", listener);
    return () => query.removeEventListener("change", listener);
  }, []);
  return theme === "dark" || (theme === "system" && systemDark);
};

const shortEuro = (value: number) =>
  value >= 1000
    ? `${(value / 1000).toLocaleString("es-ES", { maximumFractionDigits: 1 })} k€`
    : `${Math.round(value)} €`;

export const RevenueChart = ({ months }: { months: MonthBucket[] }) => {
  const isDark = useIsDark();
  const [showTable, setShowTable] = useState(false);
  const rows: ChartRow[] = months.map((month) => ({
    label: month.label,
    equipment: month.equipment,
    rental: month.rental,
    maintenance: month.maintenance,
    rest: Math.round((month.installments + month.other) * 100) / 100,
    total: month.total,
  }));
  const colors = SERIES.map((serie) => (isDark ? serie.dark : serie.light));
  const surface = isDark ? "#1a1a19" : "#ffffff";

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
          {SERIES.map((serie, index) => (
            <span key={serie.key} className="flex items-center gap-1.5">
              <span
                className="h-2.5 w-2.5 rounded-sm"
                style={{ backgroundColor: colors[index] }}
              />
              {serie.label}
            </span>
          ))}
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-xs"
          onClick={() => setShowTable((value) => !value)}
        >
          {showTable ? "Ver gráfico" : "Ver tabla"}
        </Button>
      </div>

      {showTable ? (
        <div className="overflow-x-auto">
          <table className="w-full text-sm tabular-nums">
            <thead className="text-left text-xs text-muted-foreground">
              <tr>
                <th className="py-1 pr-3 font-medium">Mes</th>
                {SERIES.map((serie) => (
                  <th
                    key={serie.key}
                    className="py-1 pr-3 text-right font-medium"
                  >
                    {serie.label}
                  </th>
                ))}
                <th className="py-1 text-right font-medium">Total</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.label} className="border-t">
                  <td className="py-1 pr-3 capitalize">{row.label}</td>
                  {SERIES.map((serie) => (
                    <td key={serie.key} className="py-1 pr-3 text-right">
                      {row[serie.key] ? formatEuro(row[serie.key]) : "–"}
                    </td>
                  ))}
                  <td className="py-1 text-right font-medium">
                    {formatEuro(row.total)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="h-[260px]">
          <ResponsiveBar
            data={rows}
            keys={SERIES.map((serie) => serie.key)}
            indexBy="label"
            colors={colors}
            margin={{ top: 20, right: 8, bottom: 28, left: 48 }}
            padding={0.35}
            borderRadius={2}
            borderWidth={1}
            borderColor={surface}
            enableLabel={false}
            enableGridY
            gridYValues={4}
            enableGridX={false}
            axisLeft={{
              tickSize: 0,
              tickPadding: 8,
              tickValues: 4,
              format: (value) => shortEuro(Number(value)),
            }}
            axisBottom={{ tickSize: 0, tickPadding: 8 }}
            theme={{
              text: { fill: "var(--color-muted-foreground)", fontSize: 11 },
              grid: {
                line: { stroke: "var(--color-border)", strokeWidth: 1 },
              },
            }}
            layers={[
              "grid",
              "axes",
              "bars",
              ({ bars }) => {
                // Month total above each stack, in text ink
                const tops = new Map<string, { x: number; y: number }>();
                bars.forEach((bar) => {
                  const key = String(bar.data.indexValue);
                  const current = tops.get(key);
                  if (!current || bar.y < current.y) {
                    tops.set(key, { x: bar.x + bar.width / 2, y: bar.y });
                  }
                });
                return (
                  <g>
                    {rows.map((row) => {
                      const top = tops.get(row.label);
                      if (!top || !row.total) return null;
                      return (
                        <text
                          key={row.label}
                          x={top.x}
                          y={top.y - 6}
                          textAnchor="middle"
                          style={{
                            fontSize: 10,
                            fill: "var(--color-muted-foreground)",
                          }}
                        >
                          {shortEuro(row.total)}
                        </text>
                      );
                    })}
                  </g>
                );
              },
            ]}
            tooltip={({ id, value, indexValue, data }) => (
              <div className="rounded-md border bg-popover px-3 py-2 text-xs text-popover-foreground shadow">
                <div className="font-medium capitalize">{indexValue}</div>
                <div>
                  {SERIES.find((serie) => serie.key === id)?.label}:{" "}
                  {formatEuro(Number(value))}
                </div>
                <div className="text-muted-foreground">
                  Total del mes: {formatEuro(Number(data.total))}
                </div>
              </div>
            )}
            role="img"
            ariaLabel="Cobros por mes de los últimos doce meses"
          />
        </div>
      )}
    </div>
  );
};
